import { join } from 'path';
import { readRepoDir, readRepoFile, repoExists, withRepoRoot } from '../core/safe-write.js';
import type { DetectedFramework, Signal } from '../core/types.js';
import { NO_CLASSIFYING_SIGNALS } from '../core/slots.js';
import { FRAMEWORKS } from './frameworks.js';
import { detectDartProject } from './dart.js';
import { detectGoProject } from './go.js';
import { detectCsharpProject, listRootCsprojs } from './csharp.js';
import { detectJvmProject, isJvmRoot } from './jvm.js';
import { detectRubyProject } from './ruby.js';
import { detectSwiftProject } from './swift.js';

interface PackageJson {
  name?: string;
  version?: string;
  description?: string;
  keywords?: string[];
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  scripts?: Record<string, string>;
  type?: string;
  main?: string;
  types?: string;
  exports?: unknown;
  bin?: string | Record<string, string>;
  private?: boolean;
  workspaces?: string[] | { packages?: string[] };
  files?: string[];
}

/** Read [package].name field from Cargo.toml — naive but adequate. */
function readCargoName(dir: string): string | null {
  const content = readRepoFile(dir, 'Cargo.toml');
  if (content === null) {return null;}
  const m = content.match(/^\s*\[package\][\s\S]*?^\s*name\s*=\s*"([^"]+)"/m);
  return m ? m[1] : null;
}

/** True if Cargo.toml declares one or more `[[bin]]` sections — Rust cli marker. */
function hasCargoBin(dir: string): boolean {
  const content = readRepoFile(dir, 'Cargo.toml');
  return content !== null && /^\s*\[\[bin\]\]/m.test(content);
}

/** Detect SDK signals — keyword-based per the SDK-priority doctrine.
 *  Returns the matched evidence string, or null if no SDK signal. */
function detectSdkSignal(dir: string, pkg: PackageJson | null): string | null {
  // pkg.keywords contains 'sdk'
  if (pkg?.keywords?.some(k => /^sdk(?:\s|-|$)|-sdk$/i.test(k))) {
    return 'package.json keywords contains "sdk"';
  }
  // pkg.name contains 'sdk' or '-sdk' suffix
  if (pkg?.name && /(?:^|[-_/])sdk(?:[-_/]|$)/i.test(pkg.name)) {
    return `package.json name "${pkg.name}" → sdk`;
  }
  // Cargo.toml [package].name contains 'sdk'
  const cargoName = readCargoName(dir);
  if (cargoName && /(?:^|[-_])sdk(?:[-_]|$)/i.test(cargoName)) {
    return `Cargo.toml name "${cargoName}" → sdk`;
  }
  return null;
}

/** Browser / Chrome extension signal — a manifest.json that declares
 *  `manifest_version` (the defining MV2/MV3 trait, absent from PWA web-app
 *  manifests). Checked at the repo root and common source/build locations,
 *  since extensions often keep the manifest under public/ or src/. */
function detectExtensionSignal(dir: string): string | null {
  const candidates = [
    'manifest.json',
    'public/manifest.json',
    'src/manifest.json',
    'app/manifest.json',
    'extension/manifest.json',
    'dist/manifest.json',
  ];
  for (const rel of candidates) {
    const text = readRepoFile(dir, rel);
    if (text === null) {continue;}
    try {
      const parsed = JSON.parse(text) as { manifest_version?: unknown };
      if (typeof parsed.manifest_version === 'number') {
        return `${rel} manifest_version ${parsed.manifest_version} (browser extension)`;
      }
    } catch { /* not valid JSON / unreadable — keep scanning */ }
  }
  return null;
}

/** Python data-science dependency detection (pyproject.toml + requirements.txt). */
function detectDataScienceSignal(dir: string): string | null {
  const dsPatterns = /(numpy|pandas|jupyter|scikit-learn|sklearn|pytorch|tensorflow|matplotlib|scipy)/i;
  const pyproject = readRepoFile(dir, 'pyproject.toml');
  if (pyproject !== null) {
    const match = pyproject.match(dsPatterns);
    if (match) {return `pyproject.toml depends on ${match[1]}`;}
  }
  const reqs = readRepoFile(dir, 'requirements.txt');
  if (reqs !== null) {
    const match = reqs.match(dsPatterns);
    if (match) {return `requirements.txt contains ${match[1]}`;}
  }
  return null;
}

/** Mobile platform signal — JS mobile (React Native / Expo / Capacitor / Ionic)
 *  + native ios/android dirs. Flutter/Dart is handled by detectDartProject. */
function detectMobileSignal(dir: string, pkg: PackageJson | null): string | null {
  const mobileDeps = ['react-native', 'expo', '@capacitor/core', '@ionic/core'];
  if (pkg) {
    for (const dep of mobileDeps) {
      if (pkg.dependencies?.[dep] || pkg.devDependencies?.[dep]) {
        return `${dep} dependency`;
      }
    }
  }
  if (repoExists(dir, 'ios') && repoExists(dir, 'android')) {
    return 'ios/ + android/ dirs';
  }
  return null;
}

/** WASM target detection (Cargo cdylib + wasm32 OR build.zig wasm OR pkg WASM build scripts). */
function detectWasmSignal(dir: string, pkg: PackageJson | null): string | null {
  const cargo = readRepoFile(dir, 'Cargo.toml');
  if (cargo !== null) {
    // crate-type = ["cdylib"] is the canonical wasm-output marker
    if (/crate-type\s*=\s*\[[^\]]*"cdylib"/.test(cargo)) {
      return 'Cargo.toml crate-type = ["cdylib"]';
    }
    // wasm-bindgen / wasm-pack as deps
    if (/wasm-bindgen|wasm-pack/i.test(cargo)) {
      return 'Cargo.toml wasm-bindgen/wasm-pack';
    }
  }
  // Zig WASM target — build.zig with .wasm or wasm in script
  const buildZig = readRepoFile(dir, 'build.zig');
  if (buildZig !== null && /\.wasm\b|wasm32|setOutputFormat\(\.wasm/i.test(buildZig)) {
    return 'build.zig wasm target';
  }
  // pkg keywords contains 'wasm' AND no app frameworks
  if (pkg?.keywords?.some(k => /^wasm(?:-|$)|webassembly/i.test(k))) {
    return 'package.json keywords contains "wasm"';
  }
  return null;
}

/** Bare HTML / vanilla site detection. */
function detectHtmlSignal(dir: string, pkg: PackageJson | null): string | null {
  // Must have index.html at root
  if (!repoExists(dir, 'index.html')) {return null;}
  // Must NOT have any frontend deps (those would classify as frontend/website/etc)
  if (pkg?.dependencies || pkg?.devDependencies) {
    const allDeps = { ...(pkg.dependencies ?? {}), ...(pkg.devDependencies ?? {}) };
    const frontendDeps = ['react', 'vue', 'svelte', '@sveltejs/kit', 'next', 'nuxt', 'astro', 'gatsby', 'solid-js', 'qwik'];
    if (frontendDeps.some(d => allDeps[d])) {return null;}
  }
  return 'index.html (vanilla, no frontend framework)';
}

/** Documentation / spec-only repo signal. */
function detectDocumentationSignal(dir: string, pkg: PackageJson | null): string | null {
  // Strong signal: name or keywords contains 'specification'
  if (pkg?.name && /specification|^@.*\/spec/i.test(pkg.name)) {
    return `package name "${pkg.name}" → documentation`;
  }
  if (pkg?.keywords?.some(k => /^(specification|format-spec|standard)$/i.test(k))) {
    return 'package keywords contains "specification"';
  }
  // A publishable CLI/bin package is never "docs-only" — even if `files` is
  // npm-publish-narrow (e.g. ["dist", "README.md"]). Bare `dist`/`src`/`bin`
  // without a trailing slash is still source, not documentation.
  // (agents-md-facts false positive: bin present + files:["dist","README.md","LICENSE"]
  // classified as documentation before the cli check could run.)
  if (pkg?.bin) {
    return null;
  }
  // pkg.files contains only md / examples / assets (no dist/, no src/)
  if (pkg?.files && pkg.files.length > 0) {
    // Match "dist", "dist/", "src/cli.ts", "*.js", etc. — bare publish dirs count as source
    const sourcePatterns =
      /^(dist|src|lib|build|bin)(\/|$)|\/(dist|src|lib|build|bin)(\/|$)|(\.|\/)(js|ts|tsx|jsx|mjs|cjs|py|rs|go)$/;
    const hasSource = pkg.files.some(f => sourcePatterns.test(f));
    const hasDocs = pkg.files.some(f => /\.(md|MD)$|examples?\//.test(f) || f === 'README.md' || f === 'docs' || f === 'docs/');
    if (!hasSource && hasDocs) {
      return 'package.json files = docs/examples only (no source)';
    }
  }
  return null;
}

/** Marketing / website signal — Astro/Gatsby static OR pkg keywords. */
function detectWebsiteSignal(dir: string, pkg: PackageJson | null, frameworks: DetectedFramework[]): string | null {
  // Astro/Gatsby are typically used for content/marketing sites
  const astroOrGatsby = frameworks.find(f => f.slug === 'astro' || f.slug === 'gatsby');
  if (astroOrGatsby) {return `${astroOrGatsby.name} (static-site framework)`;}
  // pkg.keywords explicitly marketing
  if (pkg?.keywords?.some(k => /^(website|marketing|landing|brochure|portfolio)$/i.test(k))) {
    return 'package keywords contains "website"/"marketing"/"landing"';
  }
  return null;
}

/** SAAS shape — auth + payments + database + dashboard. */
function detectSaasSignal(dir: string, pkg: PackageJson | null): string | null {
  if (!pkg) {return null;}
  const allDeps = { ...(pkg.dependencies ?? {}), ...(pkg.devDependencies ?? {}) };
  const authDeps = ['@clerk/nextjs', '@clerk/clerk-react', 'next-auth', '@auth/core', 'auth0', '@auth0/nextjs-auth0'];
  const paymentDeps = ['stripe', '@stripe/stripe-js', 'lemonsqueezy'];
  const hasAuth = authDeps.some(d => allDeps[d]);
  const hasPayment = paymentDeps.some(d => allDeps[d]);
  if (hasAuth && hasPayment) {
    return 'auth + payment dependencies';
  }
  return null;
}

/** MCPaaS (post-MCP platform) shape — multiple MCP signals + hosting + DB. */
function detectMcpaasSignal(dir: string, pkg: PackageJson | null, frameworks: DetectedFramework[]): string | null {
  // pkg.keywords contains 'mcpaas' is the strongest signal
  if (pkg?.keywords?.some(k => /mcpaas/i.test(k))) {
    return 'package keywords contains "mcpaas"';
  }
  // pkg.name contains 'mcpaas'
  if (pkg?.name && /mcpaas/i.test(pkg.name)) {
    return `package name "${pkg.name}" → mcpaas`;
  }
  // Multi-signal: MCP framework + database + hosting (a platform, not a single server)
  const hasMcp = frameworks.some(f => f.slug === 'mcp');
  const hasDb = frameworks.some(f => f.category === 'database');
  if (hasMcp && hasDb && pkg?.dependencies && Object.keys(pkg.dependencies).length > 10) {
    return 'MCP + database + complex deps (platform shape)';
  }
  return null;
}

/** Monorepo-root — private workspace with multiple packages, no single dominant FW. */
function detectMonorepoRootSignal(dir: string, pkg: PackageJson | null): string | null {
  if (!pkg?.private) {return null;}
  const hasWorkspaces = !!pkg.workspaces || repoExists(dir, 'pnpm-workspace.yaml');
  if (!hasWorkspaces) {return null;}
  return 'private workspace + multi-package';
}

/** Top-level dirs that never hold an app component. */
const SUBDIR_IGNORE = new Set([
  'node_modules', 'dist', 'build', 'out', 'vendor', 'target', 'coverage',
  'docs', 'doc', 'e2e', 'tests', 'test', '__tests__', 'scripts', 'deploy',
  'bin', 'public', 'examples', 'example', 'fixtures', 'assets', 'static',
  'migrations', 'config', 'contracts', 'proto', 'protos',
]);

const BACKEND_LANG_RE = /^(Python|Go|Rust|Ruby|Java|Kotlin)$/;

/** One app component discovered directly under the repo root. */
export interface SubdirStack {
  /** basename */
  name: string;
  /** detectLanguage() of the subdir */
  language: string;
  /** highest-confidence real-stack framework in the subdir (never Docker/CI/etc.) */
  topFramework: string | null;
  frontend: boolean;
  backend: boolean;
  /** a real-stack framework (not infra) was detected — ranks it as the "primary" */
  hasFramework: boolean;
  /** a "this dir IS the app" marker (manage.py / config.ru / bin/rails) */
  appRoot: boolean;
}

/** Framework categories that describe the app's own stack (not infra/tooling). */
const STACK_FW_CATEGORIES = new Set(['frontend', 'backend', 'database', 'css', 'ui', 'state']);

/** A directory carries a real language manifest (not just tooling/config). */
function hasLanguageManifest(d: string): boolean {
  if (
    repoExists(d, 'pyproject.toml') || repoExists(d, 'manage.py') ||
    repoExists(d, 'setup.py') || fileExists(d, 'requirements*.txt')
  ) {return true;}
  if (repoExists(d, 'go.mod')) {return true;}
  if (repoExists(d, 'Cargo.toml')) {return true;}
  if (repoExists(d, 'Gemfile')) {return true;}
  if (repoExists(d, 'pom.xml') || fileExists(d, 'build.gradle*')) {return true;}
  if (listRootCsprojs(d).length > 0) {return true;}
  const pkg = readPackageJson(d);
  if (pkg && Object.keys(pkg.dependencies ?? {}).length > 0) {return true;}
  return false;
}

/** Shallow (depth-1) scan for per-subdirectory app stacks. Feeds the polyglot
 *  classifier + `main_language` aggregation. A dir counts only when it carries a
 *  real language manifest. This is NOT monorepo tooling — it only exists to stop
 *  a repo whose real stack lives in subdirs from falling to the `library` lie. */
export function detectSubdirStacks(dir: string): SubdirStack[] {
  // The project folder bounds every read below it: a subfolder's manifest may
  // link anywhere inside the project, never out of it.
  return withRepoRoot(dir, () => subdirStacks(dir));
}

function subdirStacks(dir: string): SubdirStack[] {
  const entries = readRepoDir(dir) ?? [];
  const out: SubdirStack[] = [];
  for (const e of entries) {
    if (!e.isDirectory() || e.name.startsWith('.') || SUBDIR_IGNORE.has(e.name)) {continue;}
    const sub = join(dir, e.name);
    if (!hasLanguageManifest(sub)) {continue;}
    const language = detectLanguage(sub);
    if (!language) {continue;}
    const fws = detectFrameworks(sub).filter(f => STACK_FW_CATEGORIES.has(f.category));
    const fe = fws.find(f => f.category === 'frontend');
    const be = fws.find(f => f.category === 'backend');
    const top = fe ?? be ?? fws[0] ?? null;
    out.push({
      name: e.name,
      language,
      topFramework: top?.name ?? null,
      frontend: !!fe,
      backend: !!be || BACKEND_LANG_RE.test(language),
      hasFramework: !!top,
      appRoot:
        repoExists(sub, 'manage.py') ||
        repoExists(sub, 'config.ru') ||
        repoExists(sub, 'bin/rails') ||
        repoExists(sub, 'artisan'),
    });
  }
  return out;
}

/** Polyglot signal — ≥2 sibling app dirs, ≥2 distinct languages, a frontend AND
 *  a backend among them. Returns the `# found:` evidence string, or null. */
export function detectPolyglotSignal(dir: string, pkg: PackageJson | null): string | null {
  if (pkg?.workspaces || repoExists(dir, 'pnpm-workspace.yaml')) {return null;}
  const subs = detectSubdirStacks(dir);
  const langs = new Set(subs.map(s => s.language));
  if (subs.length < 2 || langs.size < 2) {return null;}
  if (!subs.some(s => s.frontend) || !subs.some(s => s.backend)) {return null;}
  return `polyglot: ${subs.map(s => `${s.name}/ (${s.language})`).join(', ')}`;
}

/** Aggregate the polyglot `main_language` string — primary first (a framework-
 *  bearing backend dir wins), deduped by language, roles annotated.
 *  e.g. "Python (Django · backend); JavaScript (React · frontend); Go (service)" */
export function detectPolyglotLanguage(dir: string): string | null {
  const subs = detectSubdirStacks(dir);
  if (subs.length < 2) {return null;}
  const score = (s: SubdirStack): number =>
    (s.appRoot ? 2 : 0) +
    (BACKEND_LANG_RE.test(s.language) ? 2 : 0) +
    (s.hasFramework ? 1 : 0) +
    (s.frontend ? 0.5 : 0);
  const ranked = [...subs].sort((a, b) => score(b) - score(a));
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const s of ranked) {
    if (seen.has(s.language)) {continue;}
    seen.add(s.language);
    const role = s.frontend ? 'frontend' : s.backend ? 'backend' : 'service';
    parts.push(s.topFramework ? `${s.language} (${s.topFramework} · ${role})` : `${s.language} (${role})`);
  }
  return parts.join('; ');
}

/** Read and parse package.json from a directory */
export function readPackageJson(dir: string): PackageJson | null {
  const text = readRepoFile(dir, 'package.json');
  if (text === null) {return null;}
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Scan a directory for files matching patterns */
function fileExists(dir: string, pattern: string): boolean {
  // Handle simple file checks
  if (!pattern.includes('*')) {
    return repoExists(dir, pattern);
  }

  // Handle glob patterns like "next.config.*" or ".github/workflows/*.yml"
  const parts = pattern.split('/');
  if (parts.length === 1) {
    // Simple wildcard: "next.config.*"
    const prefix = pattern.split('*')[0];
    return (readRepoDir(dir) ?? []).some(e => e.name.startsWith(prefix));
  }

  // Multi-level: ".github/workflows/*.yml"
  const subdir = join(...parts.slice(0, -1));
  const filePattern = parts[parts.length - 1];
  const prefix = filePattern.split('*')[0];
  const suffix = filePattern.split('*')[1] || '';
  return (readRepoDir(dir, subdir) ?? []).some(e => e.name.startsWith(prefix) && e.name.endsWith(suffix));
}

/** Match a single signal against project data */
function matchSignal(signal: Signal, pkg: PackageJson | null, dir: string): boolean {
  switch (signal.type) {
    case 'dependency':
      return !!(pkg?.dependencies?.[signal.key!]);
    case 'devDependency':
      return !!(pkg?.devDependencies?.[signal.key!]);
    case 'file':
      return fileExists(dir, signal.pattern!);
    case 'content': {
      // File must exist AND contain key (case-insensitive). Used so pom/gradle
      // alone never claim Spring Boot — JVM Edition kill line for tech_stack.
      if (!signal.pattern || !signal.key) {return false;}
      if (!fileExists(dir, signal.pattern)) {return false;}
      // A glob pattern names no one file: there is nothing to read, so no match.
      const body = readRepoFile(dir, signal.pattern);
      return body !== null && body.toLowerCase().includes(signal.key.toLowerCase());
    }
    default:
      return false;
  }
}

/** Detect frameworks in a directory */
export function detectFrameworks(dir: string): DetectedFramework[] {
  const pkg = readPackageJson(dir);

  return FRAMEWORKS
    .map(fw => {
      const matched = fw.signals.filter(s => matchSignal(s, pkg, dir));
      const confidence = matched.length / fw.signals.length;
      return { name: fw.name, slug: fw.slug, category: fw.category, confidence };
    })
    .filter(fw => fw.confidence > 0)
    .sort((a, b) => b.confidence - a.confidence);
}

/** Detect the primary language of a project from its manifests — `''` when
 *  there is no evidence. */
export function detectLanguage(dir: string): string {
  const pkg = readPackageJson(dir);

  // Check for TypeScript
  if (pkg?.devDependencies?.typescript || pkg?.dependencies?.typescript) {return 'TypeScript';}
  if (repoExists(dir, 'tsconfig.json')) {return 'TypeScript';}

  // Check for common language indicators
  if (repoExists(dir, 'Cargo.toml')) {return 'Rust';}
  if (repoExists(dir, 'go.mod')) {return 'Go';}
  if (listRootCsprojs(dir).length > 0) {return 'C#';}
  if (repoExists(dir, 'pyproject.toml') || repoExists(dir, 'setup.py')) {return 'Python';}
  if (repoExists(dir, 'Gemfile')) {return 'Ruby';}
  if (isJvmRoot(dir)) {
    const jvm = detectJvmProject(dir);
    if (jvm) {return jvm.mainLanguage === 'Java/Kotlin' ? 'Kotlin' : jvm.mainLanguage;}
    return 'Java';
  }
  if (repoExists(dir, 'Package.swift')) {return 'Swift';}
  if (repoExists(dir, 'build.zig')) {return 'Zig';}
  if (repoExists(dir, 'pubspec.yaml')) {return 'Dart';}

  // A package.json with none of the manifests above: JavaScript
  if (pkg) {return 'JavaScript';}

  // No evidence: nothing (never a placeholder word for project.faf)
  return '';
}

/** Project-type detection result + rationale (the #found list).
 *  Each `found` entry is a human-readable signal that contributed to the
 *  classification — emitted as a YAML comment by `writeFaf` for transparency
 *  ("Glass Hood" doctrine: the user sees WHY the cli classified as it did). */
export interface ProjectTypeDetection {
  type: string;
  /** Signals that matched, in order observed. */
  found: string[];
}

/** Detect the project type with rationale. The `found` list captures every
 *  positive signal that contributed to the classification — used downstream
 *  by `writeFaf` to render `# found: <list>` next to `type:` in the .faf.
 *
 *  Priority order per v6.6.md doctrine (most-specific first). About is a
 *  repo role (`about.represents`), not an app_type — never matches here.
 *  Rule: SDK keyword takes priority (sdk wins over library/cli/wasm). */
export function detectProjectTypeWithRationale(dir: string): ProjectTypeDetection {
  const pkg = readPackageJson(dir);
  const frameworks = detectFrameworks(dir);
  const found: string[] = [];

  // ─── 1. documentation — spec-only / docs-only repos ─────────────────────
  const docSignal = detectDocumentationSignal(dir, pkg);
  if (docSignal) {
    found.push(docSignal);
    return { type: 'documentation', found };
  }

  // ─── 2. sdk — SDK keyword/structure WINS over mcpaas/saas/wasm/cli/library ─
  // wolfejam doctrine: if a repo signals SDK (name contains "-sdk", keywords
  // contains "sdk"), it's an SDK regardless of what platform/wasm/cli signals
  // would also fire. Confirmed for mcpaas-sdk (SDK FOR the platform, not the
  // platform itself).
  const sdkSignal = detectSdkSignal(dir, pkg);
  if (sdkSignal) {
    found.push(sdkSignal);
    return { type: 'sdk', found };
  }

  // ─── 3. extension — browser/chrome extension (manifest_version) ─────────
  // manifest_version is the unambiguous MV2/MV3 trait — must beat the framework
  // heuristics below (a framework-built extension would otherwise classify as
  // svelte/frontend/fullstack — confirmed on Stack⚡️Grabber).
  const extensionSignal = detectExtensionSignal(dir);
  if (extensionSignal) {
    found.push(extensionSignal);
    return { type: 'extension', found };
  }

  // ─── 4. mcpaas — post-MCP platforms (multiple MCP signals + auth + DB) ──
  const mcpaasSignal = detectMcpaasSignal(dir, pkg, frameworks);
  if (mcpaasSignal) {
    found.push(mcpaasSignal);
    return { type: 'mcpaas', found };
  }

  // ─── 5. saas — subscription products (auth + payment) ───────────────────
  const saasSignal = detectSaasSignal(dir, pkg);
  if (saasSignal) {
    found.push(saasSignal);
    return { type: 'saas', found };
  }

  // ─── 6. wasm — WASM target in build config ──────────────────────────────
  const wasmSignal = detectWasmSignal(dir, pkg);
  if (wasmSignal) {
    found.push(wasmSignal);
    return { type: 'wasm', found };
  }

  // ─── 7. dart/flutter — content-aware pubspec classification. The same
  //        manifest backs Flutter apps, Dart CLIs, packages, servers, and MCP
  //        servers — branch by deps (Flutter→mobile, Dart MCP→mcp, Dart
  //        server→backend, CLI→cli, package→library). Unambiguous Dart. ──────
  const dart = detectDartProject(dir);
  if (dart) {
    found.push(dart.found);
    return { type: dart.appType, found };
  }

  // ─── 7a. go — content-aware go.mod classification (same composition as Dart).
  //        go.mod alone ≠ backend: MCP · Gin/Echo/… · Cobra/cmd · library. ───
  const go = detectGoProject(dir);
  if (go) {
    found.push(go.found);
    return { type: go.appType, found };
  }

  // ─── 7a2. csharp — content-aware .csproj classification.
  //        .csproj alone ≠ type: Sdk first · MCP · web/worker · CLI · library. ─
  const csharp = detectCsharpProject(dir);
  if (csharp) {
    found.push(csharp.found);
    return { type: csharp.appType, found };
  }

  // ─── 7a3. jvm — content-aware pom/gradle classification.
  //        pom/gradle alone ≠ type: plugins · parents · catalogs · MCP · Android/KMP. ─
  const jvm = detectJvmProject(dir);
  if (jvm) {
    found.push(jvm.found);
    return { type: jvm.appType, found };
  }

  // ─── 7a4. ruby — content-aware Gemfile classification.
  //        Gemfile alone ≠ Rails: gem names · layout · MCP · CLI · library. ─
  const ruby = detectRubyProject(dir);
  if (ruby) {
    found.push(ruby.found);
    return { type: ruby.appType, found };
  }

  // ─── 7a5. swift — content-aware Package.swift / Xcode classification.
  //        Package.swift alone ≠ app: products · deps · MCP · Vapor · CLI · library. ─
  const swift = detectSwiftProject(dir);
  if (swift) {
    found.push(swift.found);
    return { type: swift.appType, found };
  }

  // ─── 7b. mobile — JS mobile platform deps / native dirs ─────────────────
  const mobileSignal = detectMobileSignal(dir, pkg);
  if (mobileSignal) {
    found.push(mobileSignal);
    return { type: 'mobile', found };
  }

  // ─── 8. mcp — single MCP server ─────────────────────────────────────────
  const hasMcp = frameworks.some(f => f.slug === 'mcp');
  if (hasMcp) {
    found.push('MCP SDK signal');
    return { type: 'mcp', found };
  }

  // ─── 9. cli — Node bin / Cargo [[bin]] / Zig main.zig ───────────────────
  if (pkg?.bin) {
    found.push('package.json bin');
    return { type: 'cli', found };
  }
  if (hasCargoBin(dir)) {
    found.push('Cargo.toml [[bin]]');
    return { type: 'cli', found };
  }

  // Zig project-type detection — build.zig + entry-file convention.
  // src/main.zig → cli (executable); src/root.zig → library.
  // main.zig wins when both exist (typical: cli with internal lib exports).
  if (repoExists(dir, 'build.zig')) {
    found.push('build.zig');
    if (repoExists(dir, 'src/main.zig')) {
      found.push('src/main.zig');
      return { type: 'cli', found };
    }
    if (repoExists(dir, 'src/root.zig')) {
      found.push('src/root.zig');
      return { type: 'library', found };
    }
  }

  // ─── 10. framework — private workspace + Svelte ──────────────────────────
  const hasSvelte = frameworks.some(f => f.slug === 'svelte' || f.slug === 'sveltekit');
  const isPrivateWorkspace = pkg?.private === true && (
    repoExists(dir, 'pnpm-workspace.yaml') ||
    pkg?.workspaces !== undefined
  );
  if (isPrivateWorkspace && hasSvelte) {
    found.push('private workspace', 'Svelte');
    return { type: 'framework', found };
  }

  // ─── 11. svelte — Svelte/SvelteKit app (fullstack by nature) ────────────
  if (hasSvelte) {
    found.push('Svelte/SvelteKit');
    return { type: 'svelte', found };
  }

  // ─── 12. enterprise — private workspace + multi-FW (more specific than monorepo-root) ──
  // Must be checked BEFORE monorepo-root (which is private workspace fallback).
  const hasFrontendFw = frameworks.some(f => f.category === 'frontend');
  const hasBackendFw = frameworks.some(f => f.category === 'backend');
  if (
    pkg?.private &&
    (pkg.workspaces || repoExists(dir, 'pnpm-workspace.yaml')) &&
    hasFrontendFw && hasBackendFw
  ) {
    found.push('private workspace + frontend + backend frameworks');
    return { type: 'enterprise', found };
  }

  // ─── 13. monorepo-root — private workspace, multi-package, no specific FW story ──
  const monoSignal = detectMonorepoRootSignal(dir, pkg);
  if (monoSignal) {
    found.push(monoSignal);
    return { type: 'monorepo-root', found };
  }

  // ─── 14. website — marketing-shape (Astro/Gatsby OR keywords) ───────────
  const websiteSignal = detectWebsiteSignal(dir, pkg, frameworks);
  if (websiteSignal) {
    found.push(websiteSignal);
    return { type: 'website', found };
  }

  // ─── 15. fullstack — Next/Nuxt OR (frontend + backend) ──────────────────
  const hasNextOrNuxt = frameworks.some(f => f.slug === 'nextjs' || f.slug === 'nuxt');
  if (hasNextOrNuxt) {
    found.push(frameworks.find(f => f.slug === 'nextjs') ? 'Next.js' : 'Nuxt');
    return { type: 'fullstack', found };
  }

  const hasFrontend = frameworks.some(f => f.category === 'frontend');
  const hasBackend = frameworks.some(f => f.category === 'backend');
  if (hasFrontend && hasBackend) {
    found.push('frontend framework', 'backend framework');
    return { type: 'fullstack', found };
  }

  // ─── 16. frontend — frontend framework only ─────────────────────────────
  if (hasFrontend) {
    found.push('frontend framework');
    return { type: 'frontend', found };
  }

  // ─── 17. backend — backend framework only ───────────────────────────────
  if (hasBackend) {
    found.push('backend framework');
    return { type: 'backend', found };
  }

  // ─── 18. data-science — Python DS deps (numpy/pandas/jupyter/sklearn) ───
  const dsSignal = detectDataScienceSignal(dir);
  if (dsSignal) {
    found.push(dsSignal);
    return { type: 'data-science', found };
  }

  // ─── 19. html — bare index.html, no framework ──────────────────────────
  const htmlSignal = detectHtmlSignal(dir, pkg);
  if (htmlSignal) {
    found.push(htmlSignal);
    return { type: 'html', found };
  }

  // ─── 19b. polyglot — real stack lives in sibling app dirs (FE + BE, ≥2
  //          languages). NOT a monorepo tool (that's xai-faf-rust) — this only
  //          rescues the repo from the `library` fallback lie below. Uses the
  //          existing `fullstack` type (21 slots); no new app_type. ──────────
  const polyglotSignal = detectPolyglotSignal(dir, pkg);
  if (polyglotSignal) {
    found.push(polyglotSignal);
    return { type: 'fullstack', found };
  }

  // ─── 20. library — fallback (has main/exports but no bin) ──────────────
  if (pkg?.main && !pkg?.bin) {
    found.push('package.json main (no bin)');
    return { type: 'library', found };
  }

  // Default — bare repo with no classifying signals. The type is a
  // classification, not a slot: the file says it is the fallback (`# found:`),
  // and faf auto never writes slotignored over typed words on it.
  found.push(NO_CLASSIFYING_SIGNALS);
  return { type: 'library', found };
}

/** Detect the project type for slot mapping. Backward-compatible wrapper —
 *  returns just the type string. New code should prefer
 *  `detectProjectTypeWithRationale` to access the `#found` rationale. */
export function detectProjectType(dir: string): string {
  return detectProjectTypeWithRationale(dir).type;
}

/** Detect the runtime from a runtime file or manifest — `''` when there is
 *  no evidence. */
export function detectRuntime(dir: string): string {
  if (repoExists(dir, 'bunfig.toml')) {return 'Bun';}
  if (repoExists(dir, 'deno.json') || repoExists(dir, 'deno.jsonc')) {return 'Deno';}
  if (readPackageJson(dir)) {return 'Node.js';}
  if (repoExists(dir, 'Cargo.toml')) {return 'Rust';}
  if (repoExists(dir, 'go.mod')) {return 'Go';}
  if (listRootCsprojs(dir).length > 0) {return '.NET';}
  if (repoExists(dir, 'pubspec.yaml')) {return 'Dart';}
  return '';
}

/** The JS package managers package.json's `packageManager` field names. */
const JS_PACKAGE_MANAGERS = new Set(['npm', 'pnpm', 'yarn', 'bun']);

/** The package manager package.json's `packageManager` field declares
 *  (`"pnpm@9.1.0"`, corepack's format) — `''` when it names none. */
function declaredPackageManager(dir: string): string {
  const declared = (readPackageJson(dir) as { packageManager?: unknown } | null)?.packageManager;
  if (typeof declared !== 'string') {return '';}
  const name = /^([a-z]+)@/i.exec(declared.trim())?.[1]?.toLowerCase() ?? '';
  return JS_PACKAGE_MANAGERS.has(name) ? name : '';
}

/** The package manager a non-JS manifest implies, or `''`. */
function manifestPackageManager(dir: string): string {
  if (repoExists(dir, 'pubspec.yaml')) {return 'pub';}
  if (repoExists(dir, 'go.mod')) {return 'go modules';}
  if (listRootCsprojs(dir).length > 0) {return 'NuGet';}
  if (repoExists(dir, 'pom.xml')) {return 'maven';}
  const gradle = ['build.gradle', 'build.gradle.kts', 'settings.gradle', 'settings.gradle.kts'];
  return gradle.some(f => repoExists(dir, f)) ? 'gradle' : '';
}

/** The JS package manager a committed lockfile names, or `''`. */
const LOCKFILES: ReadonlyArray<[file: string, manager: string]> = [
  ['bun.lockb', 'bun'], ['bun.lock', 'bun'], ['pnpm-lock.yaml', 'pnpm'], ['yarn.lock', 'yarn'], ['package-lock.json', 'npm'],
];

/** Detect the package manager from repo facts — a manifest that implies one,
 *  package.json's `packageManager` field, then a lockfile. `''` when there is
 *  no evidence: a package.json alone does not say which manager installs it. */
export function detectPackageManager(dir: string): string {
  return manifestPackageManager(dir) || declaredPackageManager(dir) || (LOCKFILES.find(([f]) => repoExists(dir, f))?.[1] ?? '');
}

/** Detect CI/CD */
export function detectCicd(dir: string): string | null {
  if (repoExists(dir, '.github/workflows')) {return 'GitHub Actions';}
  if (repoExists(dir, '.gitlab-ci.yml')) {return 'GitLab CI';}
  if (repoExists(dir, '.circleci')) {return 'CircleCI';}
  if (repoExists(dir, 'Jenkinsfile')) {return 'Jenkins';}
  return null;
}

/** Detect hosting platform — from a platform's own config file. A bare
 *  Dockerfile is not one: it shows a container build, not where the app runs. */
export function detectHosting(dir: string): string | null {
  if (repoExists(dir, 'vercel.json')) {return 'Vercel';}
  if (repoExists(dir, 'netlify.toml')) {return 'Netlify';}
  if (repoExists(dir, 'wrangler.toml')) {return 'Cloudflare';}
  if (repoExists(dir, 'fly.toml')) {return 'Fly.io';}
  if (repoExists(dir, 'render.yaml')) {return 'Render';}
  return null;
}

/** Detect SvelteKit adapter from svelte.config.js */
export function detectSvelteAdapter(dir: string): string | null {
  const content = readRepoFile(dir, 'svelte.config.js');
  if (content === null) {return null;}
  // Match adapter imports: import adapter from '@sveltejs/adapter-vercel'
  // Or: import { adapter } from '@sveltejs/adapter-node'
  // Or: const adapter = require('@sveltejs/adapter-static')
  const adapterMatch = content.match(/@sveltejs\/adapter-(\w+)/);
  if (adapterMatch) {
    const adapter = adapterMatch[1];
    switch (adapter) {
      case 'vercel': return 'Vercel';
      case 'node': return 'Node';
      case 'static': return 'Static';
      case 'cloudflare': return 'Cloudflare';
      case 'netlify': return 'Netlify';
      case 'auto': return 'Auto';
      default: return adapter;
    }
  }
  return null;
}

/** Detect build tool */
export function detectBuildTool(dir: string): string | null {
  const pkg = readPackageJson(dir);
  if (pkg?.devDependencies?.vite || pkg?.dependencies?.vite) {return 'Vite';}
  if (pkg?.devDependencies?.webpack || pkg?.dependencies?.webpack) {return 'webpack';}
  if (pkg?.devDependencies?.esbuild || pkg?.dependencies?.esbuild) {return 'esbuild';}
  if (repoExists(dir, 'tsconfig.json') && pkg?.devDependencies?.typescript) {return 'TypeScript (tsc)';}
  return null;
}

/* ─── FAFb v2 chunk helpers (canonical YAML keys read by compile_fafb) ──────
 *
 * These detect content for the brick's `tech_stack`, `key_files`, and
 * `commands` chunks. `architecture` and `context` are deliberately left empty
 * for user fill (architecture overlaps with human_context.how; context is
 * free-form additional signal beyond the 6Ws).
 */

/** Detect a list of important file paths for the `key_files` chunk.
 *  Returns up to ~10 entries, ordered by canonical importance. */
export function detectKeyFiles(dir: string): string[] {
  const candidates = [
    // Manifests
    'package.json', 'Cargo.toml', 'pyproject.toml', 'go.mod',
    'build.zig', 'build.zig.zon',
    // Entry points (TS/JS)
    'src/index.ts', 'src/index.js', 'src/main.ts', 'src/cli.ts',
    // Entry points (Rust)
    'src/main.rs', 'src/lib.rs',
    // Entry points (Zig)
    'src/main.zig', 'src/root.zig',
    // Entry points (Python)
    'src/__init__.py', 'main.py', '__main__.py',
    // Entry points (Go)
    'main.go', 'cmd/main.go',
    // Specs / docs
    'README.md', 'SPECIFICATION.md', 'BINARY-FORMAT.md',
    // Config
    'tsconfig.json', 'wrangler.toml', 'vercel.json',
  ];
  return candidates.filter(f => repoExists(dir, f));
}

/** Detect build/test/lint commands for the `commands` chunk.
 *  Returns a Record<string, string> mapping command name → shell command. */
export function detectCommands(dir: string, pkg: PackageJson | null): Record<string, string> {
  const commands: Record<string, string> = {};

  // From package.json scripts (most reliable)
  if (pkg?.scripts) {
    for (const key of ['build', 'test', 'lint', 'dev', 'start']) {
      if (pkg.scripts[key]) {
        // Determine the runner — prefer bun if available, else npm
        const runner = repoExists(dir, 'bun.lock') || repoExists(dir, 'bun.lockb')
          ? 'bun run'
          : 'npm run';
        commands[key] = `${runner} ${key}`;
      }
    }
  }

  // Rust: cargo builds and tests every Cargo package. Clippy only when the
  // repo configures it — a Cargo.toml alone does not say the project lints with it.
  if (repoExists(dir, 'Cargo.toml')) {
    if (!commands.build) {commands.build = 'cargo build --release';}
    if (!commands.test) {commands.test = 'cargo test';}
    if (!commands.lint && configuresClippy(dir)) {commands.lint = 'cargo clippy';}
  }

  // Zig: build.zig is `zig build`'s own script; `zig build test` only when
  // it defines a test step.
  if (repoExists(dir, 'build.zig')) {
    if (!commands.build) {commands.build = 'zig build';}
    if (!commands.test && /\.step\(\s*"test"/.test(readText(dir, 'build.zig'))) {commands.test = 'zig build test';}
  }

  // Go: the go tool builds and tests every module.
  if (repoExists(dir, 'go.mod')) {
    if (!commands.build) {commands.build = 'go build ./...';}
    if (!commands.test) {commands.test = 'go test ./...';}
  }

  // Python: pytest only when the repo says pytest — a pyproject.toml alone
  // does not (unittest, nox and tox are as common).
  if (!commands.test && usesPytest(dir)) {commands.test = 'pytest';}

  return commands;
}

/** A file's text, or `''` when it cannot be read. */
function readText(dir: string, name: string): string {
  return readRepoFile(dir, name) ?? '';
}

/** The repo configures Clippy: a clippy.toml, or a `[lints.clippy]` table in Cargo.toml. */
function configuresClippy(dir: string): boolean {
  if (repoExists(dir, 'clippy.toml') || repoExists(dir, '.clippy.toml')) {return true;}
  return /^\s*\[(workspace\.)?lints\.clippy\]/m.test(readText(dir, 'Cargo.toml'));
}

/** The repo says pytest: pytest.ini or conftest.py, or pytest named in
 *  pyproject.toml, setup.cfg or tox.ini (its config table or a dependency). */
function usesPytest(dir: string): boolean {
  if (repoExists(dir, 'pytest.ini') || repoExists(dir, 'conftest.py')) {return true;}
  return ['pyproject.toml', 'setup.cfg', 'tox.ini'].some(f => /\bpytest\b/.test(readText(dir, f)));
}

/** Detect a tech-stack list for the `tech_stack` chunk.
 *  Combines language + frameworks + runtime into a flat list of strings. */
export function detectTechStack(
  dir: string,
  pkg: PackageJson | null,
  frameworks: DetectedFramework[],
  language: string,
): string[] {
  const stack: string[] = [];

  // Language goes first
  if (language && language !== 'Unknown') {stack.push(language);}

  // Notable frameworks (frontend / backend / database / build)
  const notableCategories = new Set(['frontend', 'backend', 'database', 'build', 'css', 'state', 'monorepo']);
  for (const fw of frameworks) {
    if (notableCategories.has(fw.category)) {
      stack.push(fw.name);
    }
  }

  // Runtime if non-trivial (Node.js by default for JS — only call out Bun/Deno/etc.)
  if (repoExists(dir, 'bunfig.toml')) {stack.push('Bun');}
  else if (repoExists(dir, 'deno.json') || repoExists(dir, 'deno.jsonc')) {stack.push('Deno');}

  // Top deps from package.json/Cargo.toml — only if stack is still sparse
  if (stack.length < 3 && pkg?.dependencies) {
    const topDeps = Object.keys(pkg.dependencies).slice(0, 5);
    for (const dep of topDeps) {
      if (!stack.some(s => s.toLowerCase().includes(dep.toLowerCase()))) {
        stack.push(dep);
      }
    }
  }

  return stack;
}
